import type { ParentAnalyticsResult, TopicMasteryRow } from "@/hooks/useParentAnalytics";
import { deriveActivityState, type ActivityState } from "@/lib/parent/activityState";
import { LAUNCH_MODULE_NAME, type LaunchModuleId, moduleOfLaunchLesson } from "@/lib/parent/launchPathRoster";

export type CommandStatusTone = "active" | "momentum" | "reengage" | "inactive" | "building";

export interface CommandStatus {
  tone: CommandStatusTone;
  label: string;
  state: ActivityState;
}

export function getCommandStatus(a: ParentAnalyticsResult, childName: string): CommandStatus {
  const now = Date.now();
  const lessonsLast7d = a.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 7 * 86400000).length;
  const lessonsLast30d = a.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 30 * 86400000).length;
  const quizzesLast30d = a.quizAttempts.filter((q) => now - new Date(q.completedAt).getTime() <= 30 * 86400000).length;
  const activity = deriveActivityState({
    childName,
    lessonsTotal: a.lessonCount,
    milestonesTotal: a.lessonActivity.length,
    quizzesTotal: a.quizAttempts.length,
    lessonsLast7d,
    lessonsLast30d,
    quizzesLast30d,
    daysSinceLastActivity: a.daysSinceLastActivity,
  });
  if (activity.state === "empty") return { tone: "building", label: "Insight Building", state: activity.state };
  if (activity.state === "reengage") return { tone: "reengage", label: "Re-engagement Needed", state: activity.state };
  if (a.daysSinceLastActivity === 0) return { tone: "active", label: "Active", state: activity.state };
  return { tone: "momentum", label: "Building Momentum", state: activity.state };
}

export function tonePillCls(tone: CommandStatusTone): string {
  switch (tone) {
    case "active": return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
    case "momentum": return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
    case "reengage": return "border-amber-400/30 bg-amber-500/10 text-amber-200";
    case "inactive": return "border-border/40 bg-muted/30 text-muted-foreground";
    case "building": return "border-primary/30 bg-primary/10 text-primary";
  }
}

/** Active-day sparkline values (last 14d, 0/1 per day). */
export function activitySparkline(a: ParentAnalyticsResult, days = 14): number[] {
  const out: number[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const active = new Set(a.activityCalendar.filter((d) => d.count > 0).map((d) => d.date));
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    out.push(active.has(`${y}-${m}-${day}`) ? 1 : 0);
  }
  return out;
}

/** Cumulative milestone sparkline (last 14d). */
export function milestoneSparkline(a: ParentAnalyticsResult, days = 14): number[] {
  const out: number[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const acts = a.lessonActivity.map((l) => new Date(l.date).getTime()).sort((x, y) => x - y);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const end = d.getTime() + 86400000;
    out.push(acts.filter((t) => t < end).length);
  }
  return out;
}

export function matchTopics(topics: TopicMasteryRow[], matchers: string[]): TopicMasteryRow[] {
  const m = matchers.map((s) => s.toLowerCase());
  return topics.filter((t) => {
    const lo = (t.topic || "").toLowerCase();
    return m.some((k) => lo.includes(k));
  });
}

/** Returns the current path label based on most-recent course activity. */
export function currentPath(a: ParentAnalyticsResult): string {
  const ip = a.courseProgress.find((c) => c.status === "In Progress");
  if (ip) return ip.courseTitle;
  const last = a.recentActivity[0]?.title;
  if (last) return last;
  if (a.topicMastery[0]?.topic) return a.topicMastery[0].topic;
  return "Money Foundations";
}

export interface PipelineModule {
  id: string;
  name: string;
  matchers: string[];
  beta: boolean;
  advanced?: boolean;
}

export const PIPELINE_MODULES: PipelineModule[] = [
  { id: "budget", name: "Budget Builder", matchers: ["money", "budget", "saving", "spending", "income"], beta: true },
  { id: "credit", name: "Credit Management", matchers: ["credit", "debt", "loan", "interest", "card"], beta: true },
  { id: "scam", name: "Scam Defense", matchers: ["scam", "fraud", "phishing", "risk"], beta: true },
  { id: "entrepreneurship", name: "Business Launch", matchers: ["entrepreneur", "business", "ownership"], beta: false, advanced: true },
];

export interface PipelineRow {
  id: string;
  name: string;
  beta: boolean;
  advanced: boolean;
  readiness: number; // 0-100
  status: "On Track" | "Building" | "Just Started" | "Checkpoint Needed" | "Locked";
  stages: { learn: StageState; checkpoint: StageState; unlock: StageState; apply: StageState };
  evidence: string;
}

export type StageState = "done" | "partial" | "locked" | "empty";

export function buildPipelineRows(a: ParentAnalyticsResult): PipelineRow[] {
  return PIPELINE_MODULES.map((m) => {
    // Phase 2 — beta modules read from explicit lesson_id rollups; never keyword-match.
    const betaStats = !m.advanced ? a.betaModuleStats[m.id as LaunchModuleId] : null;
    const lessonsCompleted = betaStats?.lessonsCompleted ?? 0;
    const lessonsTotal = betaStats?.lessonsTotal ?? 0;
    const quizCount = betaStats?.quizCount ?? 0;
    const avgQuiz = betaStats?.averageQuizScore ?? 0;
    const moduleCompleted = !!betaStats?.moduleCompleted;
    const lessonRatio = lessonsTotal > 0 ? lessonsCompleted / lessonsTotal : 0;
    // Readiness: lessons 50% · quizzes 35% · module completion 15%.
    const readiness = m.advanced
      ? 0
      : Math.round(
          lessonRatio * 50 +
            (quizCount > 0 ? Math.min(35, avgQuiz * 0.35) : 0) +
            (moduleCompleted ? 15 : 0),
        );

    let learn: StageState = "empty";
    if (lessonsCompleted > 0 && lessonRatio >= 0.6) learn = "done";
    else if (lessonsCompleted > 0) learn = "partial";

    let checkpoint: StageState = "empty";
    if (quizCount === 0) checkpoint = learn === "done" ? "empty" : "empty";
    else if (avgQuiz >= 70) checkpoint = "done";
    else checkpoint = "partial";

    const unlock: StageState = (moduleCompleted || (checkpoint === "done" && learn === "done")) ? "done" : "locked";
    const apply: StageState = moduleCompleted ? "done" : unlock === "done" ? "empty" : "locked";

    let status: PipelineRow["status"] = "Just Started";
    if (m.advanced) status = "Locked";
    else if (moduleCompleted) status = "On Track";
    else if (readiness >= 70) status = "On Track";
    else if (readiness >= 35) status = "Building";
    else if (lessonsCompleted === 0) status = "Just Started";
    else if (checkpoint === "partial") status = "Checkpoint Needed";

    const evidence = m.advanced
      ? "Advanced module — available after full launch."
      : moduleCompleted
        ? `${m.name} module completed${quizCount > 0 ? ` · ${quizCount} quiz attempt${quizCount === 1 ? "" : "s"} at ${Math.round(avgQuiz)}% average` : ""}.`
        : lessonsCompleted === 0
          ? `${m.name} hasn't started yet. Open the first lesson together.`
          : `${lessonsCompleted}/${lessonsTotal} ${m.name} lessons${quizCount > 0 ? ` · ${quizCount} quiz attempt${quizCount === 1 ? "" : "s"} at ${Math.round(avgQuiz)}% average` : ""}.`;

    return { id: m.id, name: m.name, beta: m.beta, advanced: !!m.advanced, readiness, status, stages: { learn, checkpoint, unlock, apply }, evidence };
  });
}

export interface ScorecardRow {
  id: string;
  label: string;
  percent: number;
  signal: "Strong Signal" | "Building" | "Early Signal" | "Insight Building" | "Needs Quiz" | "Not Started";
  whatItMeasures: string;
  nextStep: string;
  sampleSize: number;
}

const SCORECARD: { id: string; label: string; matchers: string[]; whatItMeasures: string }[] = [
  { id: "money", label: "Money Foundations", matchers: ["money", "budget", "saving", "spending", "income"], whatItMeasures: "Budgeting, saving, income, expenses, and basic money decisions." },
  { id: "credit", label: "Credit Foundations", matchers: ["credit", "debt", "loan", "interest", "card"], whatItMeasures: "Early understanding of credit, debt, interest, and payment history." },
  { id: "scam", label: "Scam Awareness", matchers: ["scam", "fraud", "phishing"], whatItMeasures: "Spotting scams, fraud patterns, and protecting personal information." },
  { id: "decision", label: "Decision-Making", matchers: ["decision", "tradeoff", "trade-off", "choice", "risk"], whatItMeasures: "Reading tradeoffs, weighing risk, and choosing under uncertainty." },
  { id: "module", label: "Module Readiness", matchers: [], whatItMeasures: "How close your teen is to unlocking the next hands-on module." },
];

export function buildScorecard(a: ParentAnalyticsResult, pipeline: PipelineRow[]): ScorecardRow[] {
  // Phase 2 — beta-scoped rollup per scorecard category. No keyword matching.
  // Map scorecard categories to beta modules; "decision" cuts across modules
  // and is driven by the overall first-attempt pass rate as its proxy.
  return SCORECARD.map((s) => {
    if (s.id === "module") {
      const active = pipeline.filter((p) => !p.advanced);
      const avg = active.length ? Math.round(active.reduce((sum, p) => sum + p.readiness, 0) / active.length) : 0;
      const signal: ScorecardRow["signal"] = avg >= 70 ? "Strong Signal" : avg >= 35 ? "Building" : avg > 0 ? "Early Signal" : "Not Started";
      return {
        id: s.id, label: s.label, percent: avg, signal,
        whatItMeasures: s.whatItMeasures,
        nextStep: avg >= 70 ? "Complete the readiness checkpoint to unlock the module." : "Keep working through prerequisite lessons.",
        sampleSize: active.length,
      };
    }
    let lessonsCompleted = 0, lessonsTotal = 0, quizCount = 0, avgQuiz = 0;
    if (s.id === "money" || s.id === "credit" || s.id === "scam") {
      const modKey: LaunchModuleId = s.id === "money" ? "budget" : (s.id as LaunchModuleId);
      const stats = a.betaModuleStats[modKey];
      lessonsCompleted = stats.lessonsCompleted;
      lessonsTotal = stats.lessonsTotal;
      quizCount = stats.quizCount;
      avgQuiz = stats.averageQuizScore;
    } else if (s.id === "decision") {
      // Decision-making is cross-cutting: use beta path lessons + overall pass rate.
      lessonsCompleted = a.betaLessonsCompleted;
      lessonsTotal = 10; // total curated beta lessons
      quizCount = a.quizAttempts.length;
      avgQuiz = quizCount > 0 ? a.firstAttemptPassRate : 0;
    }
    const ratio = lessonsTotal > 0 ? lessonsCompleted / lessonsTotal : 0;
    const percent = Math.round(ratio * 50 + (quizCount > 0 ? avgQuiz * 0.5 : 0));
    let signal: ScorecardRow["signal"] = "Insight Building";
    if (lessonsCompleted === 0 && quizCount === 0) signal = "Not Started";
    else if (quizCount === 0) signal = "Needs Quiz";
    else if (quizCount >= 8 && avgQuiz >= 70) signal = "Strong Signal";
    else if (quizCount >= 4) signal = "Building";
    else signal = "Early Signal";
    const nextStep = signal === "Not Started"
      ? `Open a ${s.label.toLowerCase()} lesson together this week.`
      : signal === "Needs Quiz"
        ? "Complete one quiz in this area to sharpen the signal."
        : signal === "Strong Signal"
          ? "Stretch with a harder challenge in this area."
          : "Keep practicing — more activity will deepen the signal.";
    return { id: s.id, label: s.label, percent, signal, whatItMeasures: s.whatItMeasures, nextStep, sampleSize: lessonsCompleted + quizCount };
  });
}

export function signalTone(signal: ScorecardRow["signal"]): "active" | "momentum" | "reengage" | "building" | "inactive" {
  switch (signal) {
    case "Strong Signal": return "active";
    case "Building": return "momentum";
    case "Early Signal":
    case "Insight Building":
    case "Needs Quiz": return "building";
    case "Not Started": return "inactive";
  }
}

export interface LearningMixSlice { id: string; label: string; value: number; pct: number; }

export function buildLearningMix(a: ParentAnalyticsResult): { slices: LearningMixSlice[]; total: number } {
  // Phase 2 — beta-scoped mix driven by explicit lesson_id assignment + module completions.
  const buckets: Record<string, { label: string; value: number }> = {
    money:  { label: "Money Foundations", value: a.betaModuleStats.budget.lessonsCompleted },
    credit: { label: "Credit & Debt",     value: a.betaModuleStats.credit.lessonsCompleted },
    scam:   { label: "Scam Defense",      value: a.betaModuleStats.scam.lessonsCompleted },
    modules: { label: "Hands-on Modules", value: a.betaModulesCompleted },
  };
  const total = Object.values(buckets).reduce((s, b) => s + b.value, 0);
  const slices: LearningMixSlice[] = Object.entries(buckets).map(([id, b]) => ({
    id, label: b.label, value: b.value, pct: total > 0 ? Math.round((b.value / total) * 100) : 0,
  }));
  return { slices, total };
}

export interface MilestoneEntry {
  id: string;
  kind: "completed" | "quiz_due" | "unlock";
  title: string;
  date: string;
  detail: string;
}

export function buildMilestones(a: ParentAnalyticsResult, pipeline: PipelineRow[]): MilestoneEntry[] {
  const entries: MilestoneEntry[] = [];
  for (const ev of a.recentActivity.slice(0, 3)) {
    if (ev.subtype === "LESSON_COMPLETED" || ev.subtype === "COURSE_COMPLETED") {
      entries.push({ id: ev.id, kind: "completed", title: `Completed: ${ev.title}`, date: new Date(ev.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), detail: ev.detail });
    }
  }
  // Quiz due: scorecard areas with Needs Quiz
  const nextCheckpoint = pipeline.find((p) => !p.advanced && p.stages.checkpoint !== "done" && p.stages.learn !== "empty");
  if (nextCheckpoint) {
    entries.push({ id: `quiz-${nextCheckpoint.id}`, kind: "quiz_due", title: `Quiz: ${nextCheckpoint.name}`, date: "Due soon", detail: "Checkpoint quiz unlocks the next stage." });
  }
  const nextUnlock = pipeline.find((p) => !p.advanced && p.stages.unlock !== "done");
  if (nextUnlock) {
    entries.push({ id: `unlock-${nextUnlock.id}`, kind: "unlock", title: `Unlock: ${nextUnlock.name}`, date: "Est. soon", detail: `Module unlocks after the readiness checkpoint.` });
  }
  return entries.slice(0, 5);
}